import { useEffect, useRef } from 'react';
import {
    collectGeneratingRasterStatusCheckTargets,
    nextRasterGenerationStatusCheck,
} from './mediaReviewUtils';

export type GetLessonRasterFigureCommand = (args: {
    C438_UnitId: string;
    C438_LessonId: string;
    C438_StepId: string;
    C438_FigureKey: string;
}) => void;

export function useRasterGenerationStatusCheck(
    units: Parameters<typeof collectGeneratingRasterStatusCheckTargets>[0],
    sendGetLessonRasterFigure: GetLessonRasterFigureCommand
): void {
    const sendRef = useRef(sendGetLessonRasterFigure);
    sendRef.current = sendGetLessonRasterFigure;
    const unitsRef = useRef(units);
    unitsRef.current = units;
    const firedKeysRef = useRef(new Set<string>());
    const targetSignature = collectGeneratingRasterStatusCheckTargets(units)
        .map(target => target.id)
        .join('\n');

    useEffect(() => {
        const targets = collectGeneratingRasterStatusCheckTargets(unitsRef.current);
        const currentIds = new Set(targets.map(target => target.id));
        for (const key of [...firedKeysRef.current]) {
            const separator = key.lastIndexOf(':');
            const targetId = separator >= 0 ? key.slice(0, separator) : key;
            if (!currentIds.has(targetId)) firedKeysRef.current.delete(key);
        }

        let cancelled = false;
        let timeoutId: number | undefined;

        const schedule = () => {
            const plan = nextRasterGenerationStatusCheck(
                targets,
                Date.now(),
                firedKeysRef.current
            );
            if (!plan) return;
            timeoutId = window.setTimeout(() => {
                if (cancelled) return;
                const due = nextRasterGenerationStatusCheck(
                    targets,
                    Date.now(),
                    firedKeysRef.current
                );
                if (!due) return;
                if (due.delayMs > 0) {
                    schedule();
                    return;
                }
                for (const key of due.keysToMark) firedKeysRef.current.add(key);
                for (const target of due.targetsToCheck) {
                    sendRef.current({
                        C438_UnitId: target.unitId,
                        C438_LessonId: target.lessonId,
                        C438_StepId: target.stepId,
                        C438_FigureKey: target.figureKey,
                    });
                }
                schedule();
            }, plan.delayMs);
        };

        schedule();
        return () => {
            cancelled = true;
            if (timeoutId != null) window.clearTimeout(timeoutId);
        };
    }, [targetSignature]);
}
